'use client';

import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  /**
   * When true, render content visible on first paint (no opacity-0 -> 100 transition).
   * Use for above-the-fold sections so the browser/Lighthouse can detect an LCP element
   * without waiting for IntersectionObserver to fire.
   */
  immediate?: boolean;
}

export default function ScrollReveal({
  children,
  className,
  delay = 0,
  immediate = false,
}: ScrollRevealProps) {
  const [isVisible, setIsVisible] = useState(immediate);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (immediate) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (domRef.current) {
              observer.unobserve(domRef.current);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      },
    );

    const { current } = domRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [immediate]);

  return (
    <div
      ref={domRef}
      className={cn(
        'transition-all duration-1000 ease-out transform',
        isVisible
          ? 'opacity-100 translate-y-0 scale-100'
          : 'opacity-0 translate-y-12 scale-[0.98]',
        className,
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}
